import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Loader2, AlertCircle, Chrome } from 'lucide-react';

interface AuthFormProps {
  mode: 'signIn' | 'signUp';
  onToggleMode: () => void;
}

export function AuthForm({ mode, onToggleMode }: AuthFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      const formData = new FormData(e.currentTarget);
      const email = formData.get('email') as string;
      const password = formData.get('password') as string;

      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      // Basic validation
      if (!email.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }

      if (mode === 'signUp') {
        const firstName = formData.get('firstName') as string;
        const lastName = formData.get('lastName') as string;

        if (!firstName || !lastName) {
          throw new Error('First and last name are required');
        }

        // Trim whitespace
        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();

        // Add loading message
        setMessage('Creating your account...');
        // Sign up with email
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              first_name: trimmedFirstName,
              last_name: trimmedLastName,
              name: `${trimmedFirstName} ${trimmedLastName}`,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            }
          }
        });

        if (signUpError) throw signUpError;
        // Add success message
        setMessage('Account created successfully! Signing you in...');

        // Wait briefly to allow profile creation
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Sign in immediately after signup
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password
        });

        if (signInError) throw signInError;

        // Check if sign in was successful
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          navigate('/');
        } else {
          throw new Error('Failed to get session after sign in');
        }
      } else {
        // Sign in with email
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) throw signInError;

        // Check if sign in was successful
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          navigate('/');
        } else {
          throw new Error('Failed to get session after sign in');
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      if (err instanceof Error) {
        if (err.message.includes('Email not confirmed')) {
          setError('Please check your email to confirm your account');
        } else if (err.message.includes('Invalid login credentials')) {
          setError('Invalid email or password');
        } else if (err.message.includes('Rate limit')) {
          setError('Too many attempts. Please try again later');
        } else if (err.message.includes('User already registered')) {
          setError('An account with this email already exists');
        } else if (err.message.includes('network')) {
          setError('Network error. Please check your connection and try again.');
        } else if (err.message.includes('session')) {
          setError('Session error. Please try signing in again.');
        } else {
          setError(err.message);
        }
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    try {
      const formData = new FormData(e.currentTarget);
      const email = formData.get('email') as string;

      if (!email) {
        throw new Error('Email is required');
      }

      if (!email.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;
      
      setMessage('Password reset link sent! Please check your email inbox.');
    } catch (err) {
      console.error('Password reset error:', err);
      if (err instanceof Error) {
        if (err.message.includes('rate limit')) {
          setError('Too many attempts. Please try again later.');
        } else if (err.message.includes('User not found')) {
          setError('No account found with this email address.');
        } else if (err.message.includes('network')) {
          setError('Network error. Please check your connection and try again.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      setLoading(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) throw error;
    } catch (err) {
      console.error('Google sign in error:', err);
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <button
        type="button"
        disabled={loading}
        onClick={handleGoogleSignIn}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white text-black rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
      >
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Chrome className="w-5 h-5" />
            <span>Continue with Google</span>
          </>
        )}
      </button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-600"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-[#2A2B2E] text-gray-400">or</span>
        </div>
      </div>

      {mode === 'signUp' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">First Name</label>
            <input
              type="text"
              name="firstName"
              className="w-full px-4 py-3 bg-black/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Last Name</label>
            <input
              type="text"
              name="lastName"
              className="w-full px-4 py-3 bg-black/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
              required
            />
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm text-gray-400 mb-1">Email</label>
        <input
          type="email"
          name="email"
          placeholder="Enter your email"
          className="w-full px-4 py-3 bg-black/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
          required
        />
      </div>

      {!showForgotPassword && <div>
        <label className="block text-sm text-gray-400 mb-1">Password</label>
        <input
          type="password"
          name="password"
          className="w-full px-4 py-3 bg-black/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)]"
          required
          minLength={6}
        />
      </div>}
      
      {mode === 'signIn' && !showForgotPassword && (
        <button
          type="button"
          onClick={() => {
            setShowForgotPassword(true);
            setError(null);
          }}
          className="text-sm text-[var(--accent-color)] hover:underline"
        >
          Forgot password?
        </button>
      )}

      {showForgotPassword ? (
        <div className="space-y-4">
          <div className="flex flex-col gap-4">
            <p className="text-sm text-gray-400">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--accent-color)] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                'Send Reset Link'
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowForgotPassword(false)}
              className="text-sm text-gray-400 hover:text-white"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      ) : (
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[var(--accent-color)] text-black rounded-xl py-3 font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          ) : mode === 'signIn' ? (
            'Sign In'
          ) : (
            'Create Account'
          )}
        </button>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 text-red-500 rounded-xl text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {message && (
        <div className="flex items-center gap-2 p-3 bg-green-500/10 text-green-500 rounded-xl text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p>{message}</p>
        </div>
      )}

      <div className="text-center text-sm">
        {mode === 'signIn' ? (
          <p className="text-gray-400">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={onToggleMode}
              className="text-[var(--accent-color)] hover:underline"
            >
              Sign Up
            </button>
          </p>
        ) : (
          <p className="text-gray-400">
            Already have an account?{' '}
            <button
              type="button"
              onClick={onToggleMode}
              className="text-[var(--accent-color)] hover:underline"
            >
              Sign In
            </button>
          </p>
        )}
      </div>
    </form>
  );
}